import React, { useContext, useState, useEffect } from 'react';
import { PageContext } from '@/context/PageContext';
import { SurveyClosedBanner } from 'components/Survey/SurveyClosedBanner/SurveyClosedBanner';
import { SurveyPreviewBanner } from 'components/Survey/SurveyPreviewBanner/SurveyPreviewBanner';
import { useUTM } from 'utils/useUTM';
import { useIsEmbed } from 'utils/useIsEmbed';
import { useSearchParams } from 'next/navigation';
import { SurveyExitPopup } from 'components/Survey/SurveyExitPopup/SurveyExitPopup';
import { TranslationMenu } from 'components/Atoms/TranslationMenu/TranslationMenu';
import { LanguagePreferenceToast } from 'components/LanguagePreferenceToast/LanguagePreferenceToast';
import classNames from 'classnames';
import { useFeatureFlags } from '@/context/FeatureFlagsContext';
import { getDescopeConfig } from 'utils/descopeConfig';
import { determineCountryFromProps, determineRegionFromProps } from 'utils/determineRegionFromProps';
import { determineLanguageMenuLanguages } from 'utils/determineLanguageMenuLanguages';
import { isCollabMapEntity, isEngagementLike, resolvePreviewEntity } from '@/constants/previewEntities';
import { HeaderUserMenu } from './HeaderUserMenu';
import { LiveHeader } from './LiveHeader/LiveHeader';
import { HeaderBrand } from './HeaderBrand';
import styles from './Header.module.scss';

interface Props {
  mockSearchParams?: Map<string, string>;
}

export function Header({ mockSearchParams }: Props) {
  const [showFirstPageLanguageSelector, setShowFirstPageLanguageSelector] = useState(false);
  const { theme, urlParams, slug, survey, questionnaire, organization, userInteraction, page, forceLanguageMenu } =
    useContext(PageContext);
  const featureFlags = useFeatureFlags();
  const country = determineCountryFromProps({ organization, survey, questionnaire });
  const dataCenter = determineRegionFromProps(country);
  const { projectId: descopeProjectId } = getDescopeConfig(dataCenter);
  useUTM(); // save utm codes to session storage on any page
  const { isEmbed } = useIsEmbed();
  const searchParamsHook = useSearchParams();
  const searchParams = mockSearchParams || searchParamsHook;
  const previewEntity = resolvePreviewEntity(slug, urlParams);
  const isEngagement = isEngagementLike(previewEntity);
  const isCollabMap = isCollabMapEntity(previewEntity);
  const isPresenter = searchParams.get('presenter') === 'true';
  const isSticky = !!page;

  useEffect(() => {
    const firstPageId = survey?.structure?.welcome_screen?.id || survey?.structure?.survey_entities?.[0]?.id || '';
    const firstPageElement = !!document.querySelector(`[data-testid="main-rfa-${firstPageId}"]`);
    const fingerprintConsentFormElement = !!document.querySelector(`[data-testid="fingerprint-consent-form"]`);
    const firstPageVisible = firstPageElement || fingerprintConsentFormElement;
    setShowFirstPageLanguageSelector(firstPageVisible && isEngagement && !isPresenter);
  }, [survey, isEngagement, isPresenter, userInteraction]);

  if (isEmbed) {
    return null;
  }
  // Keyed on urlParams.type rather than the route segment: /join is part of the live experience
  // and reports type 'live', but its path segment is 'join'.
  const isLiveEngagement = urlParams?.type === 'live';
  const showSurveyExitPopup = !isPresenter && isEngagement;

  const languages = determineLanguageMenuLanguages({ theme, questionnaire, survey, page });
  return (
    <>
      {descopeProjectId && country && <LanguagePreferenceToast />}
      {(isEngagement || isCollabMap) && <SurveyClosedBanner />}
      {/* ENG-4321: self-gates on urlParams.preview so every /preview route gets a banner. */}
      <SurveyPreviewBanner noContainer />
      <header
        className={classNames(styles.headerContainer, {
          [styles.engagementSpacer]: isEngagement,
          [styles.sticky]: isSticky,
        })}
      >
        <HeaderBrand />
        <div className={styles.rightSection}>
          {/** TODO: header will need to dynamically change based on slug. (currently only for surveys as other screens not implemented) */}
          {featureFlags?.residentLoginEnabled && descopeProjectId && country && !isPresenter && <HeaderUserMenu />}
          {isPresenter && isLiveEngagement && <LiveHeader />}
          {showSurveyExitPopup && (
            <div className={styles.surveyExitContainer}>
              {showFirstPageLanguageSelector && <TranslationMenu slug={slug} languages={languages} fullsize />}
              <SurveyExitPopup />
            </div>
          )}
          {(!!page || forceLanguageMenu) && <TranslationMenu slug={slug} languages={languages} fullsize />}
        </div>
      </header>
    </>
  );
}
